import { Component } from '@theme/component';
import { getScrollTop, scrollTo, getLenis } from '@theme/scroll-container';
import { StandardEvents } from '@shopify/events';

const DESKTOP = '(min-width: 990px)';

class SimucubeFiltersToggle extends Component {
  requiredRefs = ['button'];

  #wrapper = null;
  #desktop = window.matchMedia(DESKTOP);
  #isOpen = false;
  #sheetOpen = false;
  #backdrop = null;
  #closeTimer = null;
  #scrollY = 0;
  #restoreScrollY = 0;
  #scrollLockMode = null;
  #sheetLayoutBound = false;

  #sheetObserver = new MutationObserver(() => {
    if (!this.#sheetOpen) return;

    const dialog = this.#dialog();
    if (!dialog) return;

    if (!dialog.open) dialog.show();
    if (!dialog.classList.contains('theme-drawer__dialog--sheet-open')) {
      dialog.classList.add('theme-drawer__dialog--sheet-open');
    }

    const drawer = document.getElementById('filters-drawer');
    if (drawer && !drawer.style.getPropertyValue('--sc-filters-sheet-top')) {
      this.#positionSheet();
    }
  });

  #observer = new MutationObserver(() => {
    if (!this.#isOpen || !this.#desktop.matches || !this.#wrapper) return;

    if (!this.#wrapper.hasAttribute('data-filters-open')) {
      this.#wrapper.setAttribute('data-filters-open', '');
    }

    if (this.refs.button.getAttribute('aria-expanded') !== 'true') {
      this.refs.button.setAttribute('aria-expanded', 'true');
    }
  });

  connectedCallback() {
    super.connectedCallback();
    this.#wrapper = this.closest('.collection-wrapper');
    this.refs.button.addEventListener('click', this.#handleClick);
    this.#desktop.addEventListener('change', this.#handleBreakpointChange);

    if (this.#wrapper) {
      if (!this.#desktop.matches) {
        this.#wrapper.removeAttribute('data-filters-open');
      }
      this.#isOpen = this.#wrapper.hasAttribute('data-filters-open');
      this.refs.button.setAttribute('aria-expanded', String(this.#isOpen));
      this.#observer.observe(this.#wrapper, { attributes: true, subtree: true });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.button?.removeEventListener('click', this.#handleClick);
    this.#desktop.removeEventListener('change', this.#handleBreakpointChange);
    this.#observer.disconnect();
    if (this.#sheetOpen) this.#teardownSheet();
  }

  #handleClick = () => {
    if (this.#desktop.matches) {
      this.#toggleDesktopPanel();
      return;
    }

    this.#openSheet();
  };

  #dialog = () => document.querySelector('#filters-drawer .theme-drawer__dialog');

  #openSheet = () => {
    const dialog = this.#dialog();
    if (!dialog || this.#sheetOpen) return;

    if (this.#closeTimer) {
      clearTimeout(this.#closeTimer);
      this.#closeTimer = null;
    }

    this.#sheetOpen = true;
    this.#ensureBackdrop();

    document.documentElement.classList.add('sc-facets-sheet-open');

    const belowHeader = this.#isHeaderGroupExposed();
    document.documentElement.classList.toggle('sc-facets-sheet-open--below-header', belowHeader);

    this.#positionSheet();
    this.#lockPageScroll(belowHeader);
    this.#bindSheetLayout();

    if (this.#backdrop) this.#backdrop.hidden = false;
    if (!dialog.open) dialog.show();

    this.#sheetObserver.observe(dialog, { attributes: true, attributeFilter: ['class', 'open'] });
    const drawer = document.getElementById('filters-drawer');
    if (drawer) this.#sheetObserver.observe(drawer, { attributes: true, attributeFilter: ['style'] });

    requestAnimationFrame(() => {
      this.#backdrop?.classList.add('sc-facets-backdrop--visible');
      dialog.classList.add('theme-drawer__dialog--sheet-open');
    });

    const closeButton = dialog.querySelector('.sc-facets-sheet__close');
    closeButton?.addEventListener('click', this.#closeSheet);
    closeButton?.focus();
  };

  #closeSheet = () => {
    if (!this.#sheetOpen) return;

    const dialog = this.#dialog();
    dialog?.classList.remove('theme-drawer__dialog--sheet-open');
    this.#backdrop?.classList.remove('sc-facets-backdrop--visible');

    this.#closeTimer = setTimeout(() => {
      this.#closeTimer = null;
      this.#teardownSheet();
      this.refs.button?.focus();
    }, 300);
  };

  #teardownSheet = () => {
    this.#sheetOpen = false;
    this.#sheetObserver.disconnect();

    const dialog = this.#dialog();
    dialog?.classList.remove('theme-drawer__dialog--sheet-open');
    if (dialog?.open) dialog.close();

    if (this.#backdrop) {
      this.#backdrop.classList.remove('sc-facets-backdrop--visible');
      this.#backdrop.hidden = true;
    }

    document.documentElement.classList.remove('sc-facets-sheet-open', 'sc-facets-sheet-open--below-header');
    this.#unbindSheetLayout();
    this.#unlockPageScroll();
  };

  #ensureBackdrop = () => {
    if (this.#backdrop) return;

    const backdrop = document.createElement('div');
    backdrop.className = 'sc-facets-backdrop';
    backdrop.hidden = true;
    backdrop.addEventListener('click', this.#closeSheet);
    document.body.appendChild(backdrop);
    this.#backdrop = backdrop;
  };

  #isHeaderGroupExposed = () => {
    const header = document.querySelector('#header-component');
    if (!(header instanceof HTMLElement)) return false;

    if (header.dataset.stickyState === 'idle') return false;

    const headerGroupHeight =
      parseInt(getComputedStyle(document.body).getPropertyValue('--header-group-height'), 10) || 0;
    const { top, bottom } = header.getBoundingClientRect();

    return bottom > 0 && top < headerGroupHeight;
  };

  #positionSheet = () => {
    const drawer = document.getElementById('filters-drawer');
    if (!drawer) return;

    const belowHeader = this.#isHeaderGroupExposed();
    document.documentElement.classList.toggle('sc-facets-sheet-open--below-header', belowHeader);

    let top = 0;
    if (belowHeader) {
      const header = document.getElementById('header-component');
      if (header) top = Math.max(0, Math.round(header.getBoundingClientRect().bottom));
    }
    drawer.style.setProperty('--sc-filters-sheet-top', `${top}px`);
  };

  #bindSheetLayout = () => {
    if (this.#sheetLayoutBound) return;
    this.#sheetLayoutBound = true;
    window.addEventListener('resize', this.#onSheetLayoutChange, { passive: true });
    window.addEventListener('orientationchange', this.#onSheetLayoutChange, { passive: true });
    document.addEventListener('keydown', this.#onKeyDown);
    document.addEventListener(StandardEvents.collectionUpdate, this.#onFilterUpdate);
    document.addEventListener(StandardEvents.searchUpdate, this.#onFilterUpdate);
  };

  #unbindSheetLayout = () => {
    if (!this.#sheetLayoutBound) return;
    this.#sheetLayoutBound = false;
    window.removeEventListener('resize', this.#onSheetLayoutChange);
    window.removeEventListener('orientationchange', this.#onSheetLayoutChange);
    document.removeEventListener('keydown', this.#onKeyDown);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#onFilterUpdate);
    document.removeEventListener(StandardEvents.searchUpdate, this.#onFilterUpdate);
  };

  #onFilterUpdate = () => {
    if (!this.#sheetOpen) return;

    const results = this.closest('results-list') ?? this.closest('.shopify-section');
    if (!results) return;

    const headerOffset = parseInt(getComputedStyle(document.body).getPropertyValue('--header-height'), 10) || 0;
    this.#restoreScrollY = Math.max(0, Math.round(results.getBoundingClientRect().top) + this.#scrollY - headerOffset);
  };

  #onSheetLayoutChange = () => {
    if (!this.#sheetOpen) return;
    this.#positionSheet();
  };

  #onKeyDown = (event) => {
    if (event.key !== 'Escape' || event.isComposing || event.defaultPrevented) return;
    if (!this.#sheetOpen) return;

    event.preventDefault();
    this.#closeSheet();
  };

  #lockPageScroll = (preserveStickyHeader = false) => {
    this.#scrollY = getScrollTop();
    this.#restoreScrollY = this.#scrollY;

    if (preserveStickyHeader) {
      if (document.body.style.position === 'fixed') return;

      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.#scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
      this.#scrollLockMode = 'body-fixed';
      return;
    }

    if (document.documentElement.hasAttribute('scroll-lock')) return;

    document.documentElement.setAttribute('scroll-lock', '');
    this.#scrollLockMode = 'scroll-lock';
  };

  #unlockPageScroll = () => {
    const scrollY = this.#restoreScrollY;
    const mode = this.#scrollLockMode;
    this.#scrollLockMode = null;

    if (mode === 'body-fixed') {
      if (document.body.style.position !== 'fixed') return;

      document.body.style.removeProperty('position');
      document.body.style.removeProperty('top');
      document.body.style.removeProperty('left');
      document.body.style.removeProperty('right');
      document.body.style.removeProperty('width');
      getLenis()?.resize();
      scrollTo({ top: scrollY, behavior: 'instant' });
      return;
    }

    if (mode !== 'scroll-lock' || !document.documentElement.hasAttribute('scroll-lock')) return;

    document.documentElement.removeAttribute('scroll-lock');
    scrollTo({ top: scrollY, behavior: 'instant' });
  };

  #toggleDesktopPanel() {
    if (!this.#wrapper) return;

    this.#isOpen = this.#wrapper.toggleAttribute('data-filters-open');
    this.refs.button.setAttribute('aria-expanded', String(this.#isOpen));
  }

  #handleBreakpointChange = () => {
    if (this.#desktop.matches) {
      if (this.#closeTimer) {
        clearTimeout(this.#closeTimer);
        this.#closeTimer = null;
      }
      if (this.#sheetOpen) this.#teardownSheet();
      return;
    }

    this.#isOpen = false;
    this.#wrapper?.removeAttribute('data-filters-open');
    this.refs.button.setAttribute('aria-expanded', 'false');
  };
}

if (!customElements.get('simucube-filters-toggle')) {
  customElements.define('simucube-filters-toggle', SimucubeFiltersToggle);
}
